"use client";

import { useState } from "react";
import { useMemoizedFn } from "ahooks";
import { deviceDetector } from "@ospweb/sdk-ui";
import { useSnapshot } from "valtio";
import {
  buildPortfolioScreenshotChatInfo,
  getPortfolioUploadText,
} from "./portfolio";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { usePortfolioScreenshotImages } from "./usePortfolioScreenshotImages";
import type {
  PortfolioScreenshotModalConfirmPayload,
  PortfolioUploadType,
} from "@components/PortfolioScreenshotModal/types";
import type { SenderAttachment } from "@components/Sender/type";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { useAppFeatures } from "@/src/lib/bridge/useBridge";
import { hasFileUploadAccess as resolveFileUploadAccess } from "@/src/lib/bridge/filePermissions";
import { rootStore } from "@store/rootStore";

export type UsePortfolioScreenshotModalParams = {
  uploadType?: PortfolioUploadType;
  initialFiles?: File[];
};

export function usePortfolioScreenshotModal({
  uploadType = "image",
  initialFiles,
}: UsePortfolioScreenshotModalParams) {
  const { headers: authHeaders } = useRequestHeader();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { fingerprint } = useSnapshot(rootStore.user);
  const appFeatures = useAppFeatures();
  const hasFileUploadAccess =
    !deviceDetector.isApp() || resolveFileUploadAccess(appFeatures);
  const copy = getPortfolioUploadText({ uploadType });

  const [submitError, setSubmitError] = useState<string | null>(null);

  const attachmentsState = usePortfolioScreenshotImages({
    uploadType,
    authHeaders,
    fingerprint,
    isLoggedIn: !!isLoggedIn,
    hasFileUploadAccess,
    initialFiles,
    onImagesFingerprintChange: () => setSubmitError(null),
  });

  const readyAttachments: SenderAttachment[] = attachmentsState.images
    .filter((item) => item.status === "done" && !!item.url)
    .map(({ filename, mediaType, size, url }) => ({
      type: "file" as const,
      filename,
      ...(mediaType ? { mediaType } : {}),
      ...(size != null ? { size } : {}),
      url,
    }));

  const isPrimaryDisabled =
    readyAttachments.length === 0 || attachmentsState.isUploading;

  const handlePrimaryClick = useMemoizedFn(
    async (): Promise<PortfolioScreenshotModalConfirmPayload | null> => {
      if (isPrimaryDisabled) {
        return null;
      }

      try {
        const attachments = readyAttachments;
        const attachmentUrls = attachments.map((attachment) => attachment.url);
        const fileCount = attachments.length;

        trackOnboardingAnalytics("ob_funnel_upload_success", {
          file_count: fileCount,
        });

        return {
          chatInfo: buildPortfolioScreenshotChatInfo({
            uploadType,
          }),
          attachments,
          ext: {
            uploadType,
            attachmentUrls,
          },
        };
      } catch {
        setSubmitError(copy.formatErrorToast);
        return null;
      }
    },
  );

  return {
    ...attachmentsState,
    uploadType,
    copy,
    recognizeError: submitError,
    saveError: null,
    inlineError: submitError,
    primaryLabel: copy.confirmLabel,
    isPrimaryDisabled,
    isRecognizing: false,
    isSaving: false,
    isConfirmReady: readyAttachments.length > 0,
    handlePrimaryClick,
    handleConfirmAnalyze: handlePrimaryClick,
  };
}
